import type { PostLoginLanguage } from '../../i18n/postLoginLanguage';

type RhythmSectionLanguage = Extract<PostLoginLanguage, 'en' | 'es'>;

const INNERBLOOM_PREMIUM_GRADIENT =
  'linear-gradient(90deg, rgba(164, 109, 255, 0.98) 0%, rgba(204, 141, 255, 0.97) 52%, rgba(246, 187, 164, 0.96) 100%)';

const RHYTHM_SECTION_COPY = {
  en: {
    kicker: 'WEEKLY RHYTHM SYSTEM',
    title: 'Pick the intensity you can sustain',
    description:
      'Four rhythms, one weekly logic. Rhythm = weekly intensity linked to sustainable frequency.',
    rhythmLabel: 'RHYTHM',
    intensityAria: 'intensity',
    cards: [
      {
        id: 'low',
        name: 'LOW',
        frequency: '1× / week',
        state: 'low energy, overwhelmed',
        micro: 'Lower load',
        intensity: 26,
        chips: ['BASE REBUILD', 'LIGHTEST LOAD'],
      },
      {
        id: 'chill',
        name: 'CHILL',
        frequency: '2× / week',
        state: 'relaxed and stable',
        micro: 'Steady pace',
        intensity: 48,
        chips: ['STEADY CONSISTENCY', 'LIGHT CADENCE'],
      },
      {
        id: 'flow',
        name: 'FLOW',
        frequency: '3× / week',
        state: 'focused and moving',
        micro: 'Focused push',
        intensity: 72,
        chips: ['CLEAR DIRECTION', 'SUSTAINABLE PUSH'],
      },
      {
        id: 'evolve',
        name: 'EVOLVE',
        frequency: '4× / week',
        state: 'ambitious and determined',
        micro: 'More structure',
        intensity: 92,
        chips: ['HIGHER STRUCTURE', 'MORE INTENSITY'],
      },
    ],
  },
  es: {
    kicker: 'SISTEMA DE RITMO SEMANAL',
    title: 'Elegí la intensidad que puedes sostener',
    description:
      'Cuatro ritmos, una lógica semanal. Ritmo = intensidad semanal vinculada a una frecuencia sostenible.',
    rhythmLabel: 'RITMO',
    intensityAria: 'intensidad',
    cards: [
      {
        id: 'low',
        name: 'LOW',
        frequency: '1× / semana',
        state: 'poca energía, saturado',
        micro: 'Menor carga',
        intensity: 26,
        chips: ['RECONSTRUIR BASE', 'CARGA MÁS LIVIANA'],
      },
      {
        id: 'chill',
        name: 'CHILL',
        frequency: '2× / semana',
        state: 'relajado y estable',
        micro: 'Ritmo constante',
        intensity: 48,
        chips: ['CONSTANCIA ESTABLE', 'CADENCIA LIGERA'],
      },
      {
        id: 'flow',
        name: 'FLOW',
        frequency: '3× / semana',
        state: 'enfocado y en movimiento',
        micro: 'Impulso sostenido',
        intensity: 72,
        chips: ['DIRECCIÓN CLARA', 'EMPUJE SOSTENIBLE'],
      },
      {
        id: 'evolve',
        name: 'EVOLVE',
        frequency: '4× / semana',
        state: 'ambicioso y decidido',
        micro: 'Más estructura',
        intensity: 92,
        chips: ['MAYOR ESTRUCTURA', 'MÁS INTENSIDAD'],
      },
    ],
  },
} as const;

interface LabsWeeklyRhythmSystemSectionProps {
  language?: RhythmSectionLanguage;
  headingAlignment?: 'left' | 'center';
}

export function LabsWeeklyRhythmSystemSection({
  language = 'en',
  headingAlignment = 'left',
}: LabsWeeklyRhythmSystemSectionProps) {
  const copy = RHYTHM_SECTION_COPY[language];
  const isCentered = headingAlignment === 'center';

  return (
    <section aria-labelledby="labs-weekly-rhythm-title" className="relative py-2">
      <div className="pointer-events-none absolute -top-12 left-1/2 h-36 w-[80%] -translate-x-1/2 rounded-full bg-[radial-gradient(circle_at_center,rgba(176,122,255,0.24),rgba(176,122,255,0.06)_52%,transparent_78%)] blur-2xl" />

      <div className="relative space-y-8">
        <header className={`max-w-3xl space-y-3 ${isCentered ? 'mx-auto text-center' : ''}`}>
          <p className="section-kicker">{copy.kicker}</p>
          <h2 id="labs-weekly-rhythm-title" className="rhythm-heading-title text-3xl font-semibold tracking-[-0.03em] text-white md:text-4xl">
            {copy.title}
          </h2>
          <p className="rhythm-heading-sub text-sm leading-relaxed text-white/84 md:text-base">{copy.description}</p>
        </header>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {copy.cards.map((card) => (
            <article
              key={card.id}
              className="card card--hero-glass rhythm-system-card group relative flex min-h-[20.5rem] h-full flex-col gap-5 p-5 transition duration-200 hover:-translate-y-0.5"
            >
              <div className="space-y-3">
                <p className="rhythm-card-label">{copy.rhythmLabel}</p>

                <div className="flex items-start justify-between gap-3">
                  <h3 className="rhythm-card-title concept-term concept-term--rhythm">{card.name}</h3>
                  <span className="rhythm-frequency-chip">
                    {card.frequency}
                  </span>
                </div>

                <div
                  className="rhythm-intensity-track"
                  aria-label={`${card.name} ${copy.intensityAria} ${card.intensity}%`}
                >
                  <div
                    className="rhythm-intensity-fill"
                    style={{ width: `${card.intensity}%`, backgroundImage: INNERBLOOM_PREMIUM_GRADIENT }}
                  />
                </div>
              </div>

              <div className="rhythm-card-copy">
                <p className="rhythm-card-state">{card.state}</p>
                <p className="rhythm-card-micro">{card.micro}</p>
              </div>

              <ul className="mt-auto flex flex-wrap gap-1.5">
                {card.chips.map((chip) => (
                  <li
                    key={chip}
                    className="rhythm-lower-chip rounded-full px-2.5 py-1 text-[0.64rem] font-semibold uppercase tracking-[0.08em]"
                  >
                    {chip}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
